import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/main/authSlice";
import PageHeader from "../../components/PageHeader";
import Alert from "../../components/Alert";
import Spinner from "../../components/Spinner";
import Toastify from 'toastify-js';
import "toastify-js/src/toastify.css";

const CustomerPaymentResult = () => {
  const [searchParams] = useSearchParams();
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  
  // Funzione per aggiornare lo stato del pagamento nel database
  const updatePaymentStatus = async (paymentId, status) => {
    if (!paymentId) return;
    
    setUpdating(true);
    
    try {
      console.log(`Aggiornamento stato pagamento ID: ${paymentId} a ${status}`);
      
      // Recuperiamo tutti i parametri dell'URL che potrebbero contenere informazioni di Stripe
      const payment_intent = searchParams.get("payment_intent");
      const payment_intent_client_secret = searchParams.get("payment_intent_client_secret");
      const redirect_status = searchParams.get("redirect_status");
      const session_id = searchParams.get("session_id");
      
      console.log('Parametri aggiuntivi di Stripe:', { 
        payment_intent, 
        payment_intent_client_secret, 
        redirect_status, 
        session_id 
      });
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/customerPayments/update-payment-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': localStorage.getItem('installamelo-auth-token')
          },
          body: JSON.stringify({
            paymentId,
            status,
            // Dettagli di Stripe che possono essere presenti nell'URL
            stripePaymentIntentId: payment_intent,
            stripeSessionId: session_id,
            stripeStatus: redirect_status,
            // Salviamo tutti i parametri URL come transactionDetails
            transactionDetails: {
              completedAt: new Date().toISOString(),
              completedBy: user?.email || 'cliente',
              stripeRedirectParams: Object.fromEntries(searchParams.entries()), // Salviamo tutti i parametri URL
              browserInfo: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                platform: navigator.platform
              }
            }
          })
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Errore nell\'aggiornamento dello stato del pagamento:', errorText);
      } else {
        const result = await response.json();
        console.log('Stato del pagamento aggiornato con successo:', result);
      }
    } catch (error) {
      console.error('Errore durante l\'aggiornamento dello stato del pagamento:', error);
    } finally {
      setUpdating(false);
    }
  };
  
  useEffect(() => {
    // Controlla i parametri nell'URL
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const canceled = searchParams.get("canceled");
    const paymentId = searchParams.get("payment_id");
    
    console.log("CustomerPaymentResult - Parametri URL:", { success, error, canceled, paymentId });
    
    if (success === "true") {
      setIsSuccess(true);
      setMessage("Pagamento completato con successo!");
      
      // Mostra notifica toast per il pagamento riuscito
      Toastify({
        text: "Pagamento completato con successo!",
        duration: 5000,
        close: true,
        gravity: "top",
        position: "right",
        backgroundColor: "#4CAF50",
        stopOnFocus: true
      }).showToast();
      
      // Se abbiamo l'ID del pagamento, aggiorniamo lo stato nel database
      if (paymentId) {
        updatePaymentStatus(paymentId, 'completed');
      }
    } else if (canceled === "true") {
      setIsSuccess(false);
      setMessage("Pagamento annullato.");
      
      // Se abbiamo l'ID del pagamento, aggiorniamo lo stato a 'canceled'
      if (paymentId) {
        updatePaymentStatus(paymentId, 'canceled');
      }
    } else if (error) {
      setIsSuccess(false);
      setMessage(`Errore durante il pagamento: ${error}`);
      
      // Se abbiamo l'ID del pagamento, aggiorniamo lo stato a 'failed'
      if (paymentId) {
        updatePaymentStatus(paymentId, 'failed');
      }
    } else {
      setIsSuccess(false);
      setMessage("Stato del pagamento sconosciuto.");
    }
  }, [searchParams, user]);
  
  const goToPayments = () => {
    navigate("/customerPayments");
  };
  
  const goToDashboard = () => {
    navigate("/customerDashboard");
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader title={isSuccess ? "Pagamento Completato" : "Pagamento Non Riuscito"} />
      
      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-white shadow-md rounded-lg p-6">
          {updating && (
            <div className="mb-4 flex justify-center">
              <Spinner size="md" />
              <span className="ml-2 text-gray-600">Aggiornamento stato pagamento...</span>
            </div>
          )}
          
          <Alert 
            variant={isSuccess ? "success" : "danger"} 
            message={message}
          />
          
          {isSuccess && (
            <div className="my-6 text-center">
              <p className="mb-2">
                La tua transazione è stata elaborata correttamente.
              </p>
              <p>
                Riceverai una conferma via email all'indirizzo <strong>{user?.email || "registrato"}</strong>.
              </p>
            </div>
          )}
          
          {!isSuccess && (
            <div className="my-6 text-center">
              <p className="mb-2">
                Si è verificato un problema durante l'elaborazione del pagamento.
              </p>
              <p>
                Puoi riprovare più tardi o contattare il servizio clienti per assistenza.
              </p>
            </div>
          )}
          
          <div className="flex justify-center space-x-4 mt-6">
            <button
              onClick={goToPayments}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
            >
              I miei pagamenti
            </button>
            <button
              onClick={goToDashboard}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
            >
              Torna alla dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPaymentResult;
